import { useQuery } from "@tanstack/react-query";
import { checklistTemplateService } from "../services/checklistTemplate";
import type {
	ChecklistTemplateDetail,
	ChecklistTemplateLookupApiResponse,
	ChecklistTemplateQueryParams,
	TemplatesPaginatedWithCountsResponse,
} from "../types";
import { TemplateStatus } from "../types/template";

export const checklistTemplateQueryKeys = {
	all: ["checklistTemplates"] as const,
	lists: () => [...checklistTemplateQueryKeys.all] as const,
	list: (params: ChecklistTemplateQueryParams) =>
		[...checklistTemplateQueryKeys.lists(), params] as const,
	lookup: () => [...checklistTemplateQueryKeys.all, "lookup"] as const,
	details: () => ["checklistTemplate"] as const,
	detail: (id: number) =>
		[...checklistTemplateQueryKeys.details(), id] as const,
};

const TEMPLATE_DETAIL_STALE_TIME_MS = 5 * 60 * 1000;

export const useChecklistTemplatesList = (
	params: ChecklistTemplateQueryParams
) =>
	useQuery<TemplatesPaginatedWithCountsResponse>({
		queryKey: checklistTemplateQueryKeys.list(params),
		queryFn: () =>
			checklistTemplateService.getChecklistTemplateAll({
				Status: TemplateStatus.Active,
				...params,
			}),
	});

export const useChecklistTemplatesLookup = (options?: { enabled?: boolean }) =>
	useQuery<ChecklistTemplateLookupApiResponse[]>({
		queryKey: checklistTemplateQueryKeys.lookup(),
		queryFn: () => checklistTemplateService.getLookup(),
		enabled: options?.enabled ?? true,
	});

export const useChecklistTemplateById = (
	templateId: number | null,
	options?: { enabled?: boolean }
) =>
	useQuery<ChecklistTemplateDetail>({
		queryKey: checklistTemplateQueryKeys.detail(templateId ?? 0),
		queryFn: () => {
			if (templateId === null || !Number.isInteger(templateId)) {
				throw new Error("Invalid template id");
			}
			return checklistTemplateService.getTemplateById(templateId);
		},
		enabled:
			(options?.enabled ?? true) &&
			templateId !== null &&
			Number.isInteger(templateId),
		staleTime: TEMPLATE_DETAIL_STALE_TIME_MS,
	});
